import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa6';
import { GoGlobe, GoDatabase } from 'react-icons/go';
import { FaRegMap, FaBrain } from 'react-icons/fa';
import SharedFooter from '../../components/shared-footer/shared-footer';
import DisclaimerPopup from '../../components/disclaimer-popup/disclaimer-popup';

import './landing-page.css';

const PARTNERS = [
  {
    image: require('../../assets/sponsor1.png'),
    url: 'https://www.ikuluzanzibar.go.tz/',
    alt: 'Government of Zanzibar',
  },
  {
    image: require('../../assets/sponsor2.png'),
    url: 'https://zanzibartourism.go.tz/',
    alt: 'Zanzibar Commission for Tourism',
  },
  {
    image: require('../../assets/sdgailab_logo.png'),
    url: 'https://sdgailab.org',
    alt: 'SDG AI Lab',
  },
  {
    image: require('../../assets/sponsor3.png'),
    url: 'https://www.undp.org/tanzania',
    alt: 'UNDP Tanzania',
  },
];

const FEATURES = [
  {
    icon: GoGlobe,
    title: 'Real-Time Tourism Insights',
    description:
      'Capture live data on tourist sentiments, destinations, and experiences across Zanzibar.',
  },
  {
    icon: GoDatabase,
    title: 'Advanced Data Analysis',
    description:
      'Transform raw data into actionable insights for tourism businesses and policymakers.',
  },
  {
    icon: FaBrain,
    title: 'AI-Powered Predictions',
    description:
      'Leverage AI and NLP to forecast tourism trends and visitor preferences.',
  },
  {
    icon: FaRegMap,
    title: 'Geospatial Understanding',
    description:
      'Utilize Geographic Information Systems to map and analyze tourism patterns.',
  },
];

const METHOD_STEPS = [
  {
    step: '01',
    title: 'Collect',
    description:
      'Gather reviews and establishment data from leading tourism platforms covering Zanzibar.',
  },
  {
    step: '02',
    title: 'Analyse',
    description:
      'Apply NLP and sentiment models to surface what visitors praise, criticise, and request.',
  },
  {
    step: '03',
    title: 'Visualise and share',
    description:
      'Present findings in interactive dashboards that governments, tourism boards, and partners can explore.',
  },
];

const PREVIEW_POINTS = [
  'Multi-platform reviews unified in one view',
  'Aspects tracked across comfort, safety, hospitality, and more',
  'Regional maps for visits, sentiment, and pricing',
  'Export-ready charts for briefings and reports',
];

const LandingPage = () => {
  return (
    <div className="landing-page">
      <DisclaimerPopup />

      <header className="landing-topbar">
        <a href="/" className="landing-brand" aria-label="JozaniAI home">
          <img
            src={require('../../assets/jozani-logo.png')}
            alt=""
            aria-hidden="true"
          />
          <span>JozaniAI</span>
        </a>
        <nav className="landing-topbar-nav" aria-label="Landing page">
          <Link to="/about">About</Link>
          <Link to="/home" className="landing-topbar-cta">
            Explore dashboard
          </Link>
        </nav>
      </header>

      <section className="landing-hero" aria-labelledby="landing-hero-title">
        <div className="landing-container landing-hero-grid">
          <div className="landing-hero-copy">
            <p className="landing-eyebrow">Tourism intelligence · Zanzibar</p>
            <h1 id="landing-hero-title">Welcome to JozaniAI.</h1>
            <p className="landing-hero-lede">
              Enhancing Zanzibar's Tourism Sector Through Real-Time Data
              Insights
            </p>
            <div className="landing-hero-actions">
              <Link to="/home" className="landing-btn landing-btn-gold">
                Explore JozaniAI
                <FaArrowRight aria-hidden="true" />
              </Link>
              <Link to="/about" className="landing-btn landing-btn-ghost">
                About JozaniAI
              </Link>
            </div>
          </div>

          <div className="landing-hero-media">
            <div className="landing-hero-frame">
              <img
                src={require('../../assets/hero-image.jpg')}
                alt="Coastal landscape of Zanzibar at golden hour"
              />
              <div className="landing-live-chip" aria-hidden="true">
                <span className="landing-live-dot" />
                Reviews analysed across Booking, Tripadvisor, and Airbnb
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-trust" aria-label="Partners">
        <div className="landing-container landing-trust-inner">
          <p className="landing-trust-label">In partnership with</p>
          <div className="landing-trust-logos">
            {PARTNERS.map((partner) => (
              <a
                key={partner.alt}
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                className="landing-trust-link"
                title={partner.alt}
              >
                <img src={partner.image} alt={partner.alt} />
              </a>
            ))}
          </div>
        </div>
      </section>

      <section
        className="landing-section"
        aria-labelledby="landing-features-title"
      >
        <div className="landing-container">
          <div className="landing-section-head">
            <h2 id="landing-features-title">Key Technologies</h2>
          </div>
          <div className="landing-features-grid">
            {FEATURES.map((feature) => {
              const Icon = feature.icon;
              return (
                <article key={feature.title} className="landing-feature-card">
                  <div className="landing-feature-icon" aria-hidden="true">
                    <Icon size={28} />
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section
        className="landing-section landing-preview"
        aria-labelledby="landing-preview-title"
      >
        <div className="landing-container landing-preview-grid">
          <div className="landing-preview-copy">
            <p className="landing-eyebrow">Product preview</p>
            <h2 id="landing-preview-title">
              A dashboard built for high-level briefings, not just internal
              analysis.
            </h2>
            <p className="landing-body">
              Explore categories, regions, aspects, and prices in one coherent
              shell—designed so stakeholders can scan evidence quickly and leave
              with a clear story.
            </p>
            <ul className="landing-preview-list">
              {PREVIEW_POINTS.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
            <Link to="/home" className="landing-btn landing-btn-primary">
              Open JozaniAI
              <FaArrowRight aria-hidden="true" />
            </Link>
          </div>

          <div className="landing-preview-visual" aria-hidden="true">
            <div className="landing-browser">
              <div className="landing-browser-chrome">
                <span />
                <span />
                <span />
                <div className="landing-browser-url">
                  jozaniai.org / regions
                </div>
              </div>
              <div className="landing-browser-body">
                <div className="landing-mock-kpis">
                  <div className="landing-mock-kpi">
                    <span>Reviews</span>
                    <strong>187k+</strong>
                  </div>
                  <div className="landing-mock-kpi landing-mock-kpi-accent">
                    <span>Positive share</span>
                    <strong>74%</strong>
                  </div>
                  <div className="landing-mock-kpi">
                    <span>Districts</span>
                    <strong>10</strong>
                  </div>
                </div>
                <div className="landing-mock-map">
                  <div className="landing-mock-map-shape" />
                  <div className="landing-mock-map-legend">
                    <span>Visits</span>
                    <div className="landing-mock-ramp" />
                  </div>
                </div>
                <div className="landing-mock-bars">
                  <div style={{ height: '68%' }} />
                  <div style={{ height: '42%' }} />
                  <div style={{ height: '86%' }} />
                  <div style={{ height: '54%' }} />
                  <div style={{ height: '72%' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="landing-section"
        aria-labelledby="landing-method-title"
      >
        <div className="landing-container">
          <div className="landing-section-head landing-section-head-center">
            <p className="landing-eyebrow">How it works</p>
            <h2 id="landing-method-title">
              From raw reviews to decision-ready insight.
            </h2>
          </div>
          <ol className="landing-method-grid">
            {METHOD_STEPS.map((item) => (
              <li key={item.step} className="landing-method-card">
                <span className="landing-method-step">{item.step}</span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section
        className="landing-section landing-about"
        aria-labelledby="landing-about-title"
      >
        <div className="landing-container landing-about-grid">
          <div className="landing-about-visual" aria-hidden="true">
            <div className="landing-about-orb" />
            <div className="landing-about-panel">
              <p className="landing-eyebrow">Destination focus</p>
              <h3>Zanzibar</h3>
              <p>
                Built with the Revolutionary Government of Zanzibar, the
                Zanzibar Commission for Tourism, UNDP Tanzania, and the SDG AI
                Lab.
              </p>
            </div>
          </div>
          <div className="landing-about-copy">
            <h2 id="landing-about-title">About JozaniAI</h2>
            <p className="landing-body">
              JozaniAI is a joint initiative between the UNDP Tanzania
              Accelerator Lab, ICPSD SDG AI Lab and the Zanzibar Commission for
              Tourism, designed to enhance the tourism sector through real-time
              data insights. Traditional reports, like the annual International
              Visitors&apos; Exit Survey, leave a gap in timely information. To
              bridge this, the SDG AI Lab at ICPSD provides technical support,
              focusing on data analysis and interactive visualizations.
            </p>
            <p className="landing-body">
              This web platform gathers real-time data from various sources to
              capture tourist sentiments, popular destinations, preferred
              accommodations, and activities in Zanzibar.
            </p>
            <Link to="/about" className="landing-text-link">
              Read the full story
              <FaArrowRight aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      <section className="landing-cta-band" aria-labelledby="landing-cta-title">
        <div className="landing-container landing-cta-inner">
          <div>
            <p className="landing-eyebrow">Ready when you are</p>
            <h2 id="landing-cta-title">Start exploring</h2>
            <p>
              Continue to the dashboards, or read more about how the platform
              supports tourism intelligence in Zanzibar.
            </p>
          </div>
          <div className="landing-hero-actions">
            <Link to="/home" className="landing-btn landing-btn-gold">
              Explore JozaniAI
              <FaArrowRight aria-hidden="true" />
            </Link>
            <Link to="/about" className="landing-btn landing-btn-ghost">
              About JozaniAI
            </Link>
          </div>
        </div>
      </section>

      <SharedFooter showPartners={false} />
    </div>
  );
};

export default LandingPage;
